import React, { useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CustomButton from "components/CustomButton";
import FilePicker from "components/FilePicker";
import SectionCard from "components/SectionCard";
import { useSnackbar } from "hooks/useSnackbar";

const ACCENT = "#0891b2";

interface BulkError {
  row?: number;
  sheet?: string;
  group?: string;
  field?: string;
  message: string;
}

interface BulkPreviewSheet {
  sheetName?: string;
  columns: { key: string; header: string }[];
  rows: Record<string, any>[];
}

interface BulkPreview {
  success: boolean;
  totalRows: number;
  columns: { key: string; header: string }[];
  rows: Record<string, any>[];
  // Set by a multi-sheet upload (e.g. Leagues + Milestones): one table per sheet instead of columns/rows.
  sheets?: BulkPreviewSheet[] | null;
  errors: BulkError[];
}

interface BulkUploadPanelProps {
  entityLabel: string;
  templateNote: React.ReactNode;
  onDownloadTemplate: () => Promise<void>;
  onValidate: (file: File) => Promise<any>;
  onCommit: (file: File) => Promise<any>;
  onViewList: () => void;
  // "Create"/"Created" by default; pass "Update" for flows that update existing records instead.
  actionVerb?: string;
  // For a multi-sheet upload whose preview shows one sheet's rows: only that sheet's errors highlight
  // a preview row, since row numbers repeat across sheets.
  previewSheet?: string;
}

const headerCellSx = { fontWeight: 700, background: "#fafafa" };

// Template, then a file checked on selection, then a read-only review; nothing is saved before Create.
const BulkUploadPanel = ({
  entityLabel,
  templateNote,
  onDownloadTemplate,
  onValidate,
  onCommit,
  onViewList,
  actionVerb = "Create",
  previewSheet,
}: BulkUploadPanelProps) => {
  const actionVerbPast = actionVerb.endsWith("e") ? `${actionVerb}d` : `${actionVerb}ed`;
  const { showSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [created, setCreated] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);
  const selection = useRef(0);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    try {
      await work();
    } catch (error: any) {
      showSnackbar(error.message, "error");
    } finally {
      setBusy(false);
    }
  };

  const onFileSelect = (selected: File | null) => {
    const current = ++selection.current;
    setFile(selected);
    setPreview(null);
    setCreated(null);
    if (!selected) return;
    run(async () => {
      const resp = await onValidate(selected);
      // A newer file may have been chosen while this one was being checked.
      if (current === selection.current) setPreview(resp?.data);
    });
  };

  const onCreate = () =>
    run(async () => {
      const resp = await onCommit(file as File);
      const result = resp?.data;
      if (result?.success) {
        selection.current++;
        setCreated(result.created);
        setFile(null);
        setPreview(null);
        setPickerKey((key) => key + 1);
        showSnackbar(`${actionVerbPast} ${result.created} ${entityLabel}(s).`, "success");
        return;
      }
      setPreview(
        (state) =>
          state && { ...state, success: false, errors: result?.errors ?? [] },
      );
      showSnackbar(resp?.message ?? "The file failed validation.", "error");
    });

  const onCancel = () => {
    selection.current++;
    setFile(null);
    setPreview(null);
    setPickerKey((key) => key + 1);
  };

  const previewSheets: BulkPreviewSheet[] = preview
    ? preview.sheets ?? [
        { sheetName: previewSheet, columns: preview.columns, rows: preview.rows },
      ]
    : [];

  // Row numbers repeat across sheets, so a sheet's table highlights only that sheet's errors.
  const errorRowsOf = (sheetName?: string) =>
    new Set(
      (preview?.errors ?? [])
        .filter((error) => !sheetName || error.sheet === sheetName)
        .map((error) => error.row)
        .filter(Boolean),
    );

  return (
    <Box>
      <SectionCard
        step={1}
        icon={<DescriptionOutlinedIcon fontSize="small" />}
        title="Download the template"
        subtitle="Always matches the columns and rules the server checks"
        accent={ACCENT}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {templateNote}
        </Typography>
        <CustomButton
          startIcon={<ArrowDownwardIcon />}
          disabled={busy}
          onClick={() => run(onDownloadTemplate)}
        >
          Download Template
        </CustomButton>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 2 }}>
          <DescriptionOutlinedIcon
            fontSize="small"
            sx={{ color: "text.disabled", mt: "2px" }}
          />
          <Typography variant="caption" color="text.secondary">
            The template ships with an example row and an Instructions sheet
            that lists every column, whether it is required, and the accepted
            values.
          </Typography>
        </Box>
      </SectionCard>

      <SectionCard
        step={2}
        icon={<CloudUploadOutlinedIcon fontSize="small" />}
        title="Choose your completed file"
        subtitle="Every row is checked as soon as you choose the file"
        accent={ACCENT}
        isComplete={Boolean(preview?.success)}
      >
        <FilePicker
          key={pickerKey}
          label={`Select ${entityLabel}s XLSX`}
          name={`${entityLabel}BulkFile`}
          fileType=".xlsx"
          value={file}
          showPreview={false}
          onFileSelect={(selected: any) => onFileSelect(selected || null)}
        />
        {busy && !preview && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress sx={{ borderRadius: 1 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              Checking the file...
            </Typography>
          </Box>
        )}
        <Alert severity="info" sx={{ mt: 2 }}>
          All-or-nothing: nothing is {actionVerbPast.toLowerCase()} until you click{" "}
          <b>{actionVerb}</b>, and only when every row is valid.
        </Alert>
      </SectionCard>

      {preview && (
        <SectionCard
          step={3}
          icon={<FactCheckOutlinedIcon fontSize="small" />}
          title={`Review and ${actionVerb.toLowerCase()}`}
          subtitle="Rows with a problem are highlighted"
          accent={preview.success ? "#2e7d32" : "#d32f2f"}
        >
          {preview.success ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <b>
                {preview.totalRows} {entityLabel}(s)
              </b>{" "}
              ready to {actionVerb.toLowerCase()}. Review them below, then click {actionVerb}.
            </Alert>
          ) : (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Chip
                  size="small"
                  color="error"
                  label={`${preview.errors.length} issue(s)`}
                  sx={{ fontWeight: 600 }}
                />
              }
            >
              Fix the file and choose it again. Nothing has been saved.
            </Alert>
          )}

          {previewSheets.map((sheet) => {
            const errorRows = errorRowsOf(sheet.sheetName);
            return (
              <Box key={sheet.sheetName ?? "rows"}>
                {preview.sheets && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {sheet.sheetName} sheet: {sheet.rows.length} row(s)
                  </Typography>
                )}
                <TableContainer
                  sx={{
                    maxHeight: 360,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headerCellSx}>Row</TableCell>
                        {sheet.columns.map((column) => (
                          <TableCell
                            key={column.key}
                            sx={{ ...headerCellSx, whiteSpace: "nowrap" }}
                          >
                            {column.header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sheet.rows.map((row) => (
                        <TableRow
                          key={row.rowNumber}
                          sx={
                            errorRows.has(row.rowNumber)
                              ? { background: "#fdecea" }
                              : undefined
                          }
                        >
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.rowNumber}
                          </TableCell>
                          {sheet.columns.map((column) => (
                            <TableCell key={column.key} sx={{ whiteSpace: "nowrap" }}>
                              {row[column.key] ?? "-"}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            );
          })}

          {preview.errors.length > 0 && (
            <TableContainer
              sx={{
                maxHeight: 300,
                mb: 2,
                border: "1px solid #f4433655",
                borderRadius: 2,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {["Row", "Sheet", "Group", "Field", "Problem"].map(
                      (header) => (
                        <TableCell key={header} sx={headerCellSx}>
                          {header}
                        </TableCell>
                      ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.errors.map((error, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{error.row ?? "-"}</TableCell>
                      <TableCell>{error.sheet ?? "-"}</TableCell>
                      <TableCell>{error.group ?? "-"}</TableCell>
                      <TableCell>
                        {error.field ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={error.field}
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "error.main" }}>
                        {error.message}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={busy}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={busy || !preview.success}
              onClick={onCreate}
            >
              {actionVerb} {preview.totalRows} {entityLabel}(s)
            </Button>
          </Box>
        </SectionCard>
      )}

      {created !== null && (
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ borderRadius: 3, alignItems: "center" }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>
            {actionVerbPast} {created} {entityLabel}(s)
          </AlertTitle>
          {actionVerb === "Create"
            ? "They now appear in the list."
            : "The list now shows the changes."}
          <Box sx={{ mt: 1.5 }}>
            <CustomButton startIcon={<ArrowForwardIcon />} onClick={onViewList}>
              View {entityLabel}s
            </CustomButton>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default BulkUploadPanel;
